/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./auth";

interface IWebsocketProviderProps {
  children: ReactNode;
}
interface JobLog {
  id: string;
  details: Record<string, any>;
  type: string;
  message: string;
  level: 'info' | 'warning' | 'error' | 'success';
  timestamp: string;
}

interface JobProgress {
  id: string;
  details: Record<string, any>;
  idJob: number;
  logs: JobLog[];
  name: string;
  progress: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'error' | 'processing';
  type: string;
  message?: string;
  error?: string;
  createdAt?: string;
  completedAt?: string;
}

interface IWebsocketContextProps {
  jobsProgress: JobProgress[];
  RealTimeHUB: {
    on: (
      event: "newJobProgress" | "sendLocation" | "aiPathologyAnalysisCompleted",
      callback: (data: any) => void,
      identification: string
    ) => void;
  };
  socket: Socket<any, any> | null;
  isConnected: boolean;
}

export const RealTImeHUBContext = createContext<IWebsocketContextProps>(
  {} as IWebsocketContextProps
);

// const API = new NewChainAPI();

export const RealTImeHUBProvider = ({ children }: IWebsocketProviderProps) => {
  const [jobsProgress, setJobsProgress] = useState<any[]>([]);
  const [listeners, setListeners] = useState<Record<string, any[]>>({});
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();
  const socketRef = useRef<any>(null);

  const RealTimeHUB = {
    on: (
      event: "newJobProgress" | "sendLocation" | "aiPathologyAnalysisCompleted",
      callback: (data: any) => void,
      identification: string
    ) => {
      setListeners((curr: any) => {
        const updated = { ...curr };
        updated[event] = [...(curr[event] || []), { identification, callback }];
        return updated;
      });
    },
  };

  const callbackListeners = (
    event: "newJobProgress" | "receiveLocation" | "aiPathologyAnalysisCompleted",
    data: any
  ) => {
    const list = listeners[event] || [];
    for (const item of list) {
      item.callback(data);
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    const socket = socketRef.current;
    if (!socket) {
      const socket = io(import.meta.env.VITE_WS_URL, { withCredentials: true });

      socketRef.current = socket;

      socket.on("connect", () => {
        console.log("Socket conectado");
        setIsConnected(true);
      });

      socket.on("disconnect", () => {
        console.log("Socket desconectado");
        setIsConnected(false);
      });

      socket.on("changeJobProgress", (data: any) => {
        setJobsProgress((curr) => {
          const copy = [...curr];
          const index = copy.findIndex(({ idJob }) => idJob === data.idJob);
          if (index > -1) {
            copy[index] = data;
            return copy;
          }
          callbackListeners("newJobProgress", data);
          copy.push(data);
          return copy;
        });
      });

      socket.on("receiveLocation", (data: any) => {
        callbackListeners("receiveLocation", data);
      });

      socket.on("aiPathologyAnalysisCompleted", (data: any) => {
        callbackListeners("aiPathologyAnalysisCompleted", data);
      });
    }

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [user?.id, socketRef]);


  return (
    <RealTImeHUBContext.Provider
      value={{
        jobsProgress,
        RealTimeHUB,
        socket: socketRef.current,
        isConnected,
      }}
    >
      {children}
    </RealTImeHUBContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRealTimeHUB = () => useContext(RealTImeHUBContext);
